/**
 * ProjectionChartsAccordion.tsx – the three charts at the top of the Yearly Projections modal,
 * as a vertical accordion: Cashflow, Net Worth, Debt.
 *
 * "Vertical" describes the HEADINGS: each is a narrow upright strip with its label rotated a quarter
 * turn anti-clockwise (reading bottom-to-top), and the three sit side by side, left to right. The
 * open pane's chart takes the width left between them. The alternative — three full-width headings
 * stacked down the page — spends vertical space the modal needs for the projections table, and this
 * modal's content is already tall.
 *
 * One pane is open at a time, and a closed pane is UNMOUNTED rather than hidden: each chart measures
 * its own container to lay itself out, so one rendered inside a `display:none` box would measure
 * zero width and then never re-measure.
 *
 * The three panes are three configurations of one component (ProjectionChart): same zoom/pan,
 * hover tooltips, legend toggles and animated rescale in each.
 */
import React, { useEffect, useRef, useState } from 'react';
import ProjectionChart from '../../components/charts/ProjectionChart.tsx';
import { CHART_STATUS_CRITICAL, type ChartPoint, type ChartSeries } from '../../components/charts/chartTypes.ts';
import { CASHFLOW_INCOME_SERIES } from './cashflowChartData.ts';
import { DEBT_SERIES, NET_WORTH_SERIES } from './balanceSheetChartData.ts';
import type { ChartReference } from '../../components/charts/ProjectionChart.tsx';

type PaneId = 'cashflow' | 'networth' | 'debt';

/** How long a pane takes to slide across. Quick enough not to be waited on, slow enough to follow. */
const PANE_TRANSITION_MS = 280;

interface ProjectionChartsAccordionProps {
    cashflow: ChartPoint[];
    netWorth: ChartPoint[];
    debt: ChartPoint[];
    /** Full currency formatter (the modal's, so the today's-dollars toggle applies). */
    formatValue: (value: number) => string;
}

interface PaneConfig {
    id: PaneId;
    /** The strip's label — what the reader is choosing between. Kept to one word or two: it is set upright. */
    heading: string;
    /** Hover text for the strip. Too long to set upright, but worth having on the way past. */
    blurb: string;
    /** Chart name as it reads inside a control label — "Zoom in to {name} chart". */
    name: string;
    title: string;
    subtitle: string;
    ariaDescription: string;
    series: readonly ChartSeries[];
    stackLabel: string;
    stackTotalLabel: string;
    reference?: ChartReference;
    emptyMessage: string;
}

const PANES: PaneConfig[] = [
    {
        id: 'cashflow',
        heading: 'Cashflow',
        blurb: 'Income against what each year costs, and any shortfall between them.',
        name: 'cashflow',
        title: 'Cashflow by year',
        subtitle: 'Income stacked against total expenses.',
        ariaDescription:
            'stacked income bars against a dotted total-expenses line, with any shortfall shown in red. The projections table below lists the same figures.',
        series: CASHFLOW_INCOME_SERIES,
        stackLabel: 'Income',
        stackTotalLabel: 'Total income',
        reference: { id: 'expenses', label: 'Total expenses', gap: { id: 'shortfall', label: 'Shortfall', clearedLabel: 'Surplus', color: CHART_STATUS_CRITICAL } },
        emptyMessage: 'No income or expenses to chart for this projection.',
    },
    {
        id: 'networth',
        heading: 'Net Worth',
        blurb: 'What is owned each year, and what it comes to once debt is taken off.',
        name: 'net worth',
        title: 'Net Worth by year',
        subtitle: 'What is owned at the end of each year, by kind.',
        ariaDescription:
            'stacked bars of what is owned at the end of each projected year, by kind of asset. Hovering a year gives the debt owed against it and the resulting net worth. The projections table below lists the same figures.',
        series: NET_WORTH_SERIES,
        stackLabel: 'Assets',
        stackTotalLabel: 'Total assets',
        emptyMessage: 'No assets to chart for this projection.',
    },
    {
        id: 'debt',
        heading: 'Debt',
        blurb: 'Loans and other borrowings, and whether they grow or run down.',
        name: 'debt',
        title: 'Debt by year',
        subtitle: 'What is owed at the end of each year, by kind.',
        ariaDescription: 'stacked bars of what is owed at the end of each projected year, by kind of debt.',
        series: DEBT_SERIES,
        stackLabel: 'Debt',
        stackTotalLabel: 'Total debt',
        emptyMessage: 'This projection carries no loans or other debt.',
    },
];

const ProjectionChartsAccordion = ({ cashflow, netWorth, debt, formatValue }: ProjectionChartsAccordionProps) => {
    const [openPane, setOpenPane] = useState<PaneId>('cashflow');
    /**
     * The pane on its way out. Its chart stays mounted for the length of the slide, so the reader
     * watches it leave — drop it on the click instead and an empty box collapses where a chart was.
     */
    const [closingPane, setClosingPane] = useState<PaneId | null>(null);
    /**
     * The width both charts hold on to while the slide runs, in px.
     *
     * Without it each chart re-measures its box on every frame of the animation and re-lays-out to
     * suit — and a chart 20px wide breaks its legend into one row per series, so the accordion's
     * height lurches around (measured: 1031 → 473 → 863 → 411) before settling. Pinned to the width
     * the open pane has at the moment of the click — which is the width the incoming pane is heading
     * for — both charts are drawn at their final geometry from the first frame, and the box's
     * `overflow-hidden` turns the animation into a clean wipe: one chart uncovered from the left as
     * the other is cut off from the right.
     */
    const [slideWidth, setSlideWidth] = useState<number | null>(null);
    const paneRefs = useRef<Partial<Record<PaneId, HTMLDivElement | null>>>({});
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pointsFor: Record<PaneId, ChartPoint[]> = { cashflow, networth: netWorth, debt };

    useEffect(() => () => clearTimeout(closeTimer.current ?? undefined), []);

    const selectPane = (id: PaneId) => {
        if (id === openPane) return;
        setSlideWidth(paneRefs.current[openPane]?.clientWidth || null);
        setClosingPane(openPane);
        setOpenPane(id);
        clearTimeout(closeTimer.current ?? undefined);
        closeTimer.current = setTimeout(() => {
            setClosingPane(null);
            // Back to filling the box — the same width it was pinned at, so nothing reflows.
            setSlideWidth(null);
        }, PANE_TRANSITION_MS);
    };

    if (cashflow.length === 0) return null;

    return (
        // `items-stretch` (the flex default) is what gives the upright strips their height: they take
        // it from the open pane's chart rather than needing one fixed here, which would fight the
        // chart's own viewport-derived plot height.
        // The divider only ever shows where two blue strips meet — against the white chart panel it
        // is white on white, which is right: the strip's own colour is the boundary there.
        <div className="flex divide-x divide-white/25 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="projection-charts-accordion">
            {PANES.map((pane) => {
                const isOpen = openPane === pane.id;
                return (
                    <React.Fragment key={pane.id}>
                        <h4 className="flex">
                            <button
                                type="button"
                                onClick={() => selectPane(pane.id)}
                                aria-expanded={isOpen}
                                aria-controls={`projection-chart-pane-${pane.id}`}
                                id={`projection-chart-tab-${pane.id}`}
                                data-testid={`projection-chart-tab-${pane.id}`}
                                title={pane.blurb}
                                // Both steps clear 4.5:1 against white, so the label stays legible
                                // open or closed; the deeper blue is what marks the open strip out.
                                className={`flex w-11 shrink-0 items-center justify-center py-4 text-white transition-colors ${
                                    isOpen ? 'bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'
                                }`}
                            >
                                {/* A quarter turn anti-clockwise: `vertical-rl` sets the upright flow,
                                    the 180° flip makes it read bottom-to-top rather than top-to-bottom. */}
                                <span className="whitespace-nowrap text-sm font-semibold uppercase" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                                    {pane.heading}
                                </span>
                            </button>
                        </h4>
                        {/* Every pane keeps its box, at `flex-grow: 0` (so zero width) unless it is the
                            open one. Growing 0 → 1 while the outgoing box shrinks 1 → 0 on the same
                            curve is the slide: the two always sum to 1, so the strips glide across at
                            a constant total width. A box that only appeared on opening would have no
                            value to transition FROM, and would simply snap to full size.
                            `min-w-0` lets the box shrink below the chart's natural width, so the chart
                            measures the space it has been given rather than forcing the row wider. */}
                        <div
                            ref={(element) => {
                                paneRefs.current[pane.id] = element;
                            }}
                            id={`projection-chart-pane-${pane.id}`}
                            role="region"
                            aria-labelledby={`projection-chart-tab-${pane.id}`}
                            className="min-w-0 overflow-hidden transition-[flex-grow] ease-in-out motion-reduce:transition-none"
                            style={{ flexGrow: isOpen ? 1 : 0, flexBasis: 0, transitionDuration: `${PANE_TRANSITION_MS}ms` }}
                        >
                            {isOpen || closingPane === pane.id ? (
                                <div style={slideWidth ? { width: slideWidth } : undefined}>
                                    <ProjectionChart
                                        chartId={pane.id}
                                        name={pane.name}
                                        title={pane.title}
                                        subtitle={pane.subtitle}
                                        ariaDescription={pane.ariaDescription}
                                        points={pointsFor[pane.id]}
                                        series={pane.series}
                                        stackLabel={pane.stackLabel}
                                        stackTotalLabel={pane.stackTotalLabel}
                                        yAxisLabel={pane.id === 'cashflow' ? 'Dollars per year' : 'Dollars'}
                                        xAxisLabel="Year"
                                        reference={pane.reference}
                                        formatValue={formatValue}
                                        emptyMessage={pane.emptyMessage}
                                    />
                                </div>
                            ) : null}
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default ProjectionChartsAccordion;
